import { Box, Button, IconButton, Paper, Stack, Typography, styled, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { ILocalDisplay } from "../../models/requests/local/localDisplay";
import EditOnly from "../permissions/EditOnly";
import StrictModeDroppable from "./StrictModeDroppable";

const RightContent = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const TopBar = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#fff",
  paddingInline: 20,
  paddingBlock: 16,
  borderRadius: 3,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const ScrollableList = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  padding: "24px 28px 0",
  minHeight: 0,
}));

const TimelineColumn = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: 40,
  flexShrink: 0,
  position: "relative",
}));

const TimelineLine = styled(Box)(() => ({
  width: 2,
  flex: 1,
  backgroundColor: "rgba(0,0,0,0.12)",
}));

const TimelineCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isHovered",
})<{ isHovered?: boolean }>(({ theme, isHovered }) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  border: `2px solid ${isHovered ? theme.palette.primary.main : "rgba(0,0,0,0.12)"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: 13,
  color: isHovered ? theme.palette.primary.main : "rgba(0, 0, 0, 0.5)",
  backgroundColor: "#fff",
  zIndex: 1,
  flexShrink: 0,
  transition: "all 0.2s ease",
}));

const LocalCard = styled(Paper)(() => ({
  flex: 1,
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.08)",
  backgroundColor: "#fff",
  padding: "12px 16px",
  margin: "6px 0",
}));

const DragHandle = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  marginRight: 12,
  color: "rgba(0,0,0,0.3)",
  cursor: "grab",
}));

const ROUTE_LOCALS_DROPPABLE_ID = "route-locals";

interface RouteLocalsSectionProps {
  propertyGroupId: number;
  selectedLocals: ILocalDisplay[];
  onOpenPicker: () => void;
  onRemoveLocal: (localId: number) => void;
  onReorderLocals: (nextLocals: ILocalDisplay[]) => void;
}

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const RouteLocalsSection = ({
  propertyGroupId,
  selectedLocals,
  onOpenPicker,
  onRemoveLocal,
  onReorderLocals,
}: RouteLocalsSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const hasLocals = selectedLocals.length > 0;
  const canAddLocals = propertyGroupId > 0;

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.droppableId !== ROUTE_LOCALS_DROPPABLE_ID) return;
    if (result.source.index === result.destination.index) return;

    const next = reorder(selectedLocals, result.source.index, result.destination.index);
    onReorderLocals(next);
  };

  return (
    <RightContent>
      <TopBar>
        <EditOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onOpenPicker}
            disabled={!canAddLocals}
            color="primary"
            sx={{
              borderRadius: 2,
              height: 38,
              px: 2.5,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {t("table.route.addLocal")}
          </Button>
        </EditOnly>
      </TopBar>

      <ScrollableList>
        <DragDropContext onDragEnd={onDragEnd}>
          <StrictModeDroppable droppableId={ROUTE_LOCALS_DROPPABLE_ID}>
            {(droppableProvided: any) => (
              <Box ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                sx={{ position: "relative" }}>
                {selectedLocals.map((local, index) => (
                  <Draggable key={local.id} draggableId={String(local.id)} index={index}>
                    {(draggableProvided, snapshot) => {
                      const isActive = snapshot.isDragging;

                      return (
                        <Box
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={{
                            display: "flex",
                            alignItems: "stretch",
                            position: "relative",
                            "&:hover .local-card": {
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 6px 18px ${theme.palette.primary.main}33`,
                            },
                            "&:hover .timeline-circle": {
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <TimelineColumn>
                            {index > 0 ? <TimelineLine /> : <Box sx={{ flex: 1 }} />}
                            <TimelineCircle
                              className="timeline-circle"
                              isHovered={isActive}
                            >
                              {index + 1}
                            </TimelineCircle>
                            {index < selectedLocals.length - 1 ? <TimelineLine /> : <Box sx={{ flex: 1 }} />}
                          </TimelineColumn>

                          <LocalCard
                            className="local-card"
                            elevation={0}
                            sx={{
                              ml: 1.5,
                              borderColor: isActive ? theme.palette.primary.main : "rgba(0,0,0,0.08)",
                              boxShadow: isActive
                                ? `0 8px 24px ${theme.palette.primary.main}33`
                                : "none",
                              transition: "all 0.2s ease",
                              "&:hover .delete-btn": { opacity: 1 },
                            }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
                                <EditOnly>
                                  <DragHandle {...draggableProvided.dragHandleProps}>
                                    <DragIndicatorIcon sx={{ fontSize: 20 }} />
                                  </DragHandle>
                                </EditOnly>

                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                                    {local.name}
                                  </Typography>
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <LocationOnOutlinedIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                                    <Typography color="text.secondary" noWrap sx={{ fontSize: 12 }}>
                                      {local.floorName}, {local.buildingName}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>

                              <EditOnly>
                                <IconButton
                                  className="delete-btn"
                                  size="small"
                                  onClick={() => onRemoveLocal(local.id)}
                                  sx={{
                                    opacity: 0,
                                    transition: "opacity 0.15s ease",
                                    color: "rgba(0,0,0,0.35)",
                                    "&:hover": { color: "error.main" },
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </EditOnly>
                            </Stack>
                          </LocalCard>
                        </Box>
                      );
                    }}
                  </Draggable>
                ))}

                {droppableProvided.placeholder}
              </Box>
            )}
          </StrictModeDroppable>
        </DragDropContext>

        {canAddLocals && (
          <EditOnly>
            <Box
              onClick={onOpenPicker}
              sx={{
                borderRadius: 3,
                border: "2px dashed rgba(0, 0, 0, 0.10)",
                padding: "20px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "rgba(0, 0, 0, 0.4)",
                transition: "all 0.15s ease",
                mt: 2,
                mb: 2,
                "&:hover": {
                  borderColor: "rgba(25, 118, 210, 0.35)",
                  color: "#1976d2",
                  backgroundColor: "rgba(25, 118, 210, 0.02)",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 18, mr: 0.75 }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                {t("table.route.addLocal")}
              </Typography>
            </Box>
          </EditOnly>
        )}

        {!hasLocals && !canAddLocals && (
          <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 1 }}>
              {t("table.route.noLocals")}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {t("table.route.noLocalsNoPGHint")}
            </Typography>
          </Box>
        )}

        {!hasLocals && canAddLocals && (
          <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 1 }}>
              {t("table.route.noLocals")}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {t("table.route.noLocalsHint")}
            </Typography>
          </Box>
        )}
      </ScrollableList>
    </RightContent>
  );
};

export default RouteLocalsSection;